// ─── src/api/meetupProposals.ts ──────────────────────────────────────────────

import { apiClient } from "./registration";
import type {
  MeetupProposal,
  MeetupProposalList,
  MeetupProposalParams,
  MeetupProposalStats,
} from "../types/meetupProposal";
import type { PaginationMeta } from "../types/registration";

const BASE = "/meetup-proposals";

export interface NormalisedMeetupProposals {
  data: MeetupProposal[];
  meta: PaginationMeta;
}

const normalise = (raw: MeetupProposalList): NormalisedMeetupProposals => ({
  data: raw.data,
  meta: {
    total:      raw.total,
    page:       raw.page,
    limit:      raw.limit,
    totalPages: Math.ceil(raw.total / raw.limit),
  },
});

export const getMeetupProposals = async (
  params: MeetupProposalParams,
): Promise<NormalisedMeetupProposals> => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== null),
  );
  const { data } = await apiClient.get<MeetupProposalList>(BASE, { params: clean });
  return normalise(data);
};

export const getMeetupProposalStats = async (): Promise<MeetupProposalStats> => {
  const { data } = await apiClient.get<MeetupProposalStats>(`${BASE}/stats`);
  return data;
};

/** Approve a proposal and publish it as a public event (image URL required). */
/**
 * Approve a meetup proposal. `imageUrl` is optional — the public GEW
 * events page renders meetups with a default banner when none is set.
 */
export const approveMeetupProposal = async (
  id: string,
  imageUrl?: string,
): Promise<{ success: boolean; message: string; eventId: string }> => {
  const payload = imageUrl ? { imageUrl } : {};
  const { data } = await apiClient.post(`${BASE}/${id}/approve`, payload);
  return data;
};

/** Reject a proposal (un-publishes its event too, if one existed). */
export const rejectMeetupProposal = async (id: string): Promise<void> => {
  await apiClient.post(`${BASE}/${id}/reject`);
};

export const deleteMeetupProposal = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${id}`);
};

/**
 * Admin edit on a pending proposal. Mirrors the backend
 * `UpdateMeetupProposalDto` shape — every field optional, PATCH semantics.
 */
export interface UpdateMeetupProposalPayload {
  mode?: "in_person" | "online" | "hybrid";
  meetingUrl?: string;
  eventTitle?: string;
  targetAudience?: string;
  eventFormat?: string;
  eventFormatOther?: string;
  motivation?: string;
  agenda?: string;
  structure?: string;
  seriesDetails?: string;
  speakers?: string;
  proposedDate?: string;
  startTime?: string;
  durationHours?: string;
  venue?: string;
  venueStatus?: string;
  expectedAttendees?: string;
  otherDetails?: string;
  hostGovernorate?: string;
  hostCityArea?: string;
  coHostName?: string;
  coHostEmail?: string;
  additionalCoHosts?: string;
  partnerSupport?: string;
  partnershipDetails?: string;
  logisticsSupport?: string;
  wantsFacebookEvent?: boolean;
}

export const updateMeetupProposal = async (
  id: string,
  payload: UpdateMeetupProposalPayload,
): Promise<MeetupProposal> => {
  const { data } = await apiClient.patch<MeetupProposal>(`${BASE}/${id}`, payload);
  return data;
};
